import { createContext, useContext, useEffect, useState } from 'react';
import { CircuitNode, CircuitSchema, CoolantFluid } from '../types';
import { apiService } from './apiService';
import { findCircuits } from './ports';

/**
 * The coolant of a circuit is chosen on its pump (`properties.fluid_id`); the
 * backend solves every circuit with its pump's coolant. Schemas saved before
 * that carried one canvas-wide `simulation_params.fluid_id`, which
 * normalizeSchema copies onto pumps that have none.
 */

let fluidsPromise: Promise<CoolantFluid[]> | null = null;

/** Coolant library, fetched once per page load and shared by every caller. */
export function loadFluids(): Promise<CoolantFluid[]> {
  if (!fluidsPromise) {
    fluidsPromise = apiService.getFluids().catch(err => {
      fluidsPromise = null; // allow a retry after a failed load
      throw err;
    });
  }
  return fluidsPromise;
}

export function useFluids(): { fluids: CoolantFluid[]; error: boolean } {
  const [fluids, setFluids] = useState<CoolantFluid[]>([]);
  const [error, setError] = useState(false);
  useEffect(() => {
    let alive = true;
    loadFluids()
      .then(list => { if (alive) setFluids(list); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, []);
  return { fluids, error };
}

/** Fluid id -> name, for components that only need to label a pump. */
export const FluidNamesContext = createContext<Record<number, string>>({});
export const useFluidNames = () => useContext(FluidNamesContext);

/** Coolant a new pump starts with: Water-Glycol 50/50 when the library has it. */
export function defaultFluidId(fluids: CoolantFluid[], preferred?: number | null): number | null {
  if (preferred && fluids.some(f => f.id === preferred)) return preferred;
  const glycol = fluids.find(f => /50\s*\/\s*50/.test(f.name));
  return (glycol ?? fluids[0])?.id ?? null;
}

const isPump = (n: CircuitNode) => n.data?.component?.component_type === 'pump';

/** Pumps without a coolant get `fluidId`; returns null when nothing changes. */
export function withPumpCoolants(schema: CircuitSchema, fluidId: number | null): CircuitSchema | null {
  if (!fluidId) return null;
  let changed = false;
  const nodes = schema.nodes.map(n => {
    if (!isPump(n) || n.data.properties?.fluid_id) return n;
    changed = true;
    return { ...n, data: { ...n.data, properties: { ...n.data.properties, fluid_id: fluidId } } };
  });
  return changed ? { ...schema, nodes } : null;
}

/**
 * Set the coolant of the circuit `nodeId` belongs to: every pump of that
 * circuit gets it, so a loop with two pumps never ends up with two coolants.
 */
export function setCircuitCoolant(schema: CircuitSchema, nodeId: string, fluidId: number): CircuitSchema {
  const circuit = findCircuits(schema.nodes, schema.edges).find(c => c.nodeIds.includes(nodeId));
  const members = new Set(circuit ? circuit.nodeIds : [nodeId]);
  return {
    ...schema,
    nodes: schema.nodes.map(n =>
      members.has(n.id) && isPump(n)
        ? { ...n, data: { ...n.data, properties: { ...n.data.properties, fluid_id: fluidId } } }
        : n
    ),
  };
}
